import React, { useEffect, useRef, useState } from "react";
import { Loading, Spinner } from "../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import ConfirmModal from "../components/ConfirmModal";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  deleteCategory,
  fetchCategoryDetail,
  selectCategoryById,
  selectCategoryErrorById,
  selectCategoryStatusById,
  selectDeleteCategoryErrorById,
  selectDeleteCategoryStatusById,
  selectUpdateCategoryErrorById,
  selectUpdateCategoryStatusById,
  updateCategory,
} from "../features/categories/categoriesSlice";

export default function CategoryEditPage() {
  const { categoryId } = useParams();
  const id = String(categoryId);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const category = useSelector(selectCategoryById(id));
  const loadStatus = useSelector(selectCategoryStatusById(id));
  const loadError = useSelector(selectCategoryErrorById(id));
  const saveStatus = useSelector(selectUpdateCategoryStatusById(id));
  const saveError = useSelector(selectUpdateCategoryErrorById(id));
  const delStatus = useSelector(selectDeleteCategoryStatusById(id));
  const delError = useSelector(selectDeleteCategoryErrorById(id));

  const [form, setForm] = useState({ name: "", description: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchCategoryDetail({ categoryId: id }));
  }, [dispatch, id]);

  useEffect(() => {
    if (!category) return;
    setForm({ name: category.name ?? "", description: category.description ?? "" });
  }, [category]);

  const canSave = loadStatus === "succeeded" && saveStatus !== "saving";
  const canDelete = delStatus !== "saving";

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    e.target.value = "";
  };

  const onUploadImage = async () => {
    if (!imageFile) return;
    setImageUploading(true);
    const fd = new FormData();
    fd.append("image", imageFile);
    const res = await dispatch(updateCategory({ categoryId: id, patch: fd }));
    setImageUploading(false);
    if (updateCategory.fulfilled.match(res)) {
      setImageFile(null);
      setImagePreview(null);
      dispatch(fetchCategoryDetail({ categoryId: id }));
    }
  };

  const onRemoveImagePreview = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const onSave = async () => {
    const patch = {
      name: String(form.name || "").trim(),
      description: String(form.description || ""),
    };
    const res = await dispatch(updateCategory({ categoryId: id, patch }));
    if (updateCategory.fulfilled.match(res)) {
      dispatch(fetchCategoryDetail({ categoryId: id }));
    }
  };

  const onDelete = async () => {
    const res = await dispatch(deleteCategory({ categoryId: id }));
    if (deleteCategory.fulfilled.match(res)) {
      const from = location.state?.from;
      if (typeof from === "string" && from.startsWith("/")) navigate(from);
      else navigate("/warehouse/categories");
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Редактирование категории</div>
          <div style={styles.subTitle}>
            Категория <span style={styles.mono}>#{id}</span> (данные из <span style={styles.mono}>/api/product/categories/</span>).
          </div>
        </div>
        <div style={styles.headerActions}>
          <button
            type="button"
            onClick={() => {
              const from = location.state?.from;
              if (typeof from === "string" && from.startsWith("/")) {
                navigate(from);
                return;
              }
              if (window.history.length > 1) {
                navigate(-1);
                return;
              }
              navigate("/warehouse/categories");
            }}
            style={styles.btnGhost}
          >
            ← Назад
          </button>
          <button type="button" onClick={() => dispatch(fetchCategoryDetail({ categoryId: id }))} style={styles.btn}>
            Обновить
          </button>
          <button type="button" onClick={() => setConfirmOpen(true)} style={styles.btnDanger} disabled={!canDelete}>
            {delStatus === "saving" ? "Удаление…" : "Удалить"}
          </button>
          <button type="button" onClick={onSave} style={styles.btnPrimary} disabled={!canSave}>
            {saveStatus === "saving" ? "Сохранение…" : "Сохранить"}
          </button>
        </div>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title="Удалить категорию?"
        message="Это действие нельзя отменить. Категория будет удалена навсегда."
        confirmLabel="Удалить"
        danger
        onConfirm={() => { setConfirmOpen(false); onDelete(); }}
        onCancel={() => setConfirmOpen(false)}
      />

      {loadStatus === "loading" && <Loading text="Загрузка…" />}
      {loadStatus === "failed" && <div style={styles.error}>{loadError}</div>}
      {saveStatus === "failed" && <div style={styles.error}>{String(saveError || "Failed to save")}</div>}
      {delStatus === "failed" && <div style={styles.error}>{String(delError || "Failed to delete")}</div>}

      <div style={styles.body}>
        <div style={styles.card}>
          <div style={styles.grid}>
            <div style={styles.field}>
              <div style={styles.label}>ID</div>
              <div style={styles.valueMono}>{category?.id ?? "—"}</div>
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Slug</div>
              <div style={styles.valueMono}>{category?.slug ?? "—"}</div>
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.formGrid}>
            <div style={styles.formField}>
              <div style={styles.label}>Name</div>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                style={styles.input}
                placeholder="Category name"
              />
            </div>
            <div style={styles.formFieldWide}>
              <div style={styles.label}>Description</div>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                style={styles.textarea}
                placeholder="Description"
                rows={5}
              />
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={{ ...styles.label, marginBottom: 12 }}>ФОТО КАТЕГОРИИ</div>
          <div style={styles.imageRow}>
            {(imagePreview || category?.image) && (
              <div style={styles.imageWrap}>
                <img
                  src={imagePreview || category.image}
                  alt=""
                  style={styles.imageThumb}
                />
                {imagePreview && (
                  <div style={styles.imageNewBadge}>новое</div>
                )}
              </div>
            )}
            <div style={styles.imageControls}>
              {!imagePreview && !category?.image && (
                <div style={styles.hint}>Фото не добавлено</div>
              )}
              <button
                type="button"
                style={styles.btn}
                onClick={() => fileInputRef.current?.click()}
              >
                {category?.image ? "Заменить фото" : "Загрузить фото"}
              </button>
              {imagePreview && (
                <>
                  <button
                    type="button"
                    style={styles.btnPrimary}
                    onClick={onUploadImage}
                    disabled={imageUploading}
                  >
                    {imageUploading ? (
                      <>
                        <Spinner size="sm" /> Сохранение…
                      </>
                    ) : (
                      "Сохранить фото"
                    )}
                  </button>
                  <button
                    type="button"
                    style={styles.btnGhost}
                    onClick={onRemoveImagePreview}
                  >
                    Отмена
                  </button>
                </>
              )}
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={onPickImage}
          />
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { height: "100%", display: "flex", flexDirection: "column", background: "var(--c-bg)", color: "var(--c-text)" },
  mono: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  header: {
    padding: "16px 18px",
    borderBottom: "1px solid var(--c-border)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
  },
  title: { fontSize: 20, fontWeight: 900 },
  subTitle: { marginTop: 6, opacity: 0.75, fontSize: 14, lineHeight: 1.35, maxWidth: 760 },
  headerActions: { display: "flex", gap: 10, alignItems: "center" },
  btn: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  btnPrimary: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid rgba(245,197,24,0.3)",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  btnDanger: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid rgba(255, 80, 80, 0.30)",
    background: "rgba(255, 80, 80, 0.16)",
    color: "rgba(255, 200, 200, 0.95)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  btnGhost: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  notice: { padding: "12px 18px", fontSize: 15, opacity: 0.85 },
  error: {
    padding: "12px 18px",
    fontSize: 15,
    background: "var(--c-danger-dim)",
    color: "var(--c-danger)",
    borderTop: "1px solid var(--c-danger)",
    borderBottom: "1px solid var(--c-danger)",
  },
  body: { padding: 18, minHeight: 0, overflow: "auto", display: "grid", gap: 12 },
  card: {
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    borderRadius: 16,
    padding: 14,
  },
  grid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 },
  field: { display: "grid", gap: 6 },
  label: { fontSize: 13, color: "var(--c-text-dim)", fontWeight: 900 },
  valueMono: {
    fontSize: 15,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 },
  formField: { display: "grid", gap: 6 },
  formFieldWide: { display: "grid", gap: 6, gridColumn: "1 / -1" },
  input: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
  },
  textarea: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    resize: "vertical",
  },
  imageRow: { display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" },
  imageWrap: { position: "relative", flexShrink: 0 },
  imageThumb: {
    width: 120,
    height: 120,
    objectFit: "cover",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    display: "block",
  },
  imageNewBadge: {
    position: "absolute",
    top: 6,
    left: 6,
    background: "var(--c-accent)",
    color: "var(--c-accent-on)",
    fontSize: 12,
    fontWeight: 900,
    padding: "2px 7px",
    borderRadius: 999,
  },
  imageControls: { display: "flex", flexDirection: "column", gap: 8, justifyContent: "center" },
  hint: { fontSize: 15, opacity: 0.7, fontWeight: 800 },
};

